import { SuggestionForSearchAndEvidence } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  formatSuggestedPersonDisplayName,
  getSuggestedActorDisplayName,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.utils';
import { AiSuggestionKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

import { EntityType } from '../entities/types/EntityType';

/**
 * Minimal suggestion shape for grouping by the explicit `entity`/`testamentaryEntity`
 * edge that the save layer stamps on a suggestion. Satisfied structurally by the
 * lightweight Estate V2 fragments and the full review fragments.
 */
export interface SuggestionForEntityEdgeGrouping {
  entity?: { id: string } | null;
  testamentaryEntity?: { id: string } | null;
}

/**
 * Groups AI suggestions by their owning entity or testamentary entity ID, read
 * from the explicit edges on the suggestion. Suggestions without either edge
 * are dropped.
 *
 * Holdings suggestions use their explicit `entity` edge as well, so Estate page
 * grouping does not need KGV2 statement traversal.
 */
export function groupSuggestionsByEntityOrTestamentaryEntityID<
  T extends SuggestionForEntityEdgeGrouping,
>(suggestions: T[]): Record<string, T[]> {
  return suggestions.reduce<Record<string, T[]>>((acc, suggestion) => {
    const ownerId = suggestion.entity?.id || suggestion.testamentaryEntity?.id;
    if (!ownerId) return acc;
    if (!acc[ownerId]) acc[ownerId] = [];
    acc[ownerId]!.push(suggestion);
    return acc;
  }, {});
}

/**
 * Formats the suggestion as a search query string.
 *
 * When the user clicks on a suggestion to locate the evidence, we prefill the
 * DocumentSearchBar input text with the suggestion, and use it to navigate
 * between the pieces of evidence.
 *
 * @param suggestion
 * @param entityType
 */
export function getSuggestionAsSearchQuery(
  suggestion: SuggestionForSearchAndEvidence,
  entityType: EntityType | null | undefined
): string {
  if (!suggestion) {
    return '';
  }

  switch (suggestion.kind) {
    // For suggested actors, use the suggested display name.
    case AiSuggestionKind.Beneficiary:
      return `${getSuggestedActorDisplayName(suggestion)} as current beneficiary`;
    case AiSuggestionKind.BeneficiaryV2:
      return `${getSuggestedActorDisplayName(suggestion)} as current beneficiary`;
    case AiSuggestionKind.Principal: {
      const label = entityTypeToPrincipalLabel(entityType);
      const name = getSuggestedActorDisplayName(suggestion);
      return label ? `${name} as ${label}` : name;
    }
    case AiSuggestionKind.TrustAdvisor:
      return `${getSuggestedActorDisplayName(suggestion)} as trust advisor`;
    case AiSuggestionKind.Trustee:
      return `${getSuggestedActorDisplayName(suggestion)} as trustee`;
    case AiSuggestionKind.SuccessorTrustee:
      return `${getSuggestedActorDisplayName(suggestion)} as successor trustee`;
    case AiSuggestionKind.BeneficialOwner:
      return `${getSuggestedActorDisplayName(suggestion)} as beneficial owner`;
    case AiSuggestionKind.KeyPerson:
      return `${getSuggestedActorDisplayName(suggestion)} as key person`;
    case AiSuggestionKind.DpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.DpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_1DiesFirst:
    case AiSuggestionKind.TeDpRecipientGrantor_2DiesFirst:
    case AiSuggestionKind.DpRecipientV2:
      return `${getSuggestedActorDisplayName(suggestion)} as recipient`;

    // For single-value suggestions, use the field's label string.
    case AiSuggestionKind.EffectiveDate:
      return `Effective date`;
    case AiSuggestionKind.GstStatus:
      return `GST status`;
    case AiSuggestionKind.JurisdictionStateCode:
      return `Governing law`;
    case AiSuggestionKind.LegalName:
      return `Legal name`;
    case AiSuggestionKind.CrummeyWithdrawalPeriodDays:
      return `Crummey withdrawal period`;
    case AiSuggestionKind.DoingBusinessAsName:
      return `Doing business as name`;
    case AiSuggestionKind.FincenId:
      return `FinCEN ID`;
    case AiSuggestionKind.TaxId:
      return `Tax ID (EIN)`;
    case AiSuggestionKind.BusinessEntityTaxStatus:
      return `Tax status`;
    case AiSuggestionKind.BeneficialOwnersAsOfDate:
      return `Beneficial owners as of date`;
    case AiSuggestionKind.OnboardingEntity:
      return 'onboardingEntity' in suggestion
        ? (suggestion.onboardingEntity?.displayName ??
            sentenceCaseWithAcronyms(
              suggestion.onboardingEntity?.entityName || 'Entity'
            ))
        : 'Entity';
    case AiSuggestionKind.OnboardingIndividual:
      return 'onboardingIndividual' in suggestion
        ? formatSuggestedPersonDisplayName(suggestion.onboardingIndividual)
        : `Individual`;
    case AiSuggestionKind.OnboardingOrganization:
      return 'onboardingOrganization' in suggestion
        ? (suggestion.onboardingOrganization?.displayName ??
            sentenceCaseWithAcronyms(
              suggestion.onboardingOrganization?.organizationName ||
                'Organization'
            ))
        : 'Organization';
    case AiSuggestionKind.OnboardingTestamentaryEntity:
      return 'onboardingTestamentaryEntity' in suggestion
        ? (suggestion.onboardingTestamentaryEntity?.displayName ??
            sentenceCaseWithAcronyms(
              suggestion.onboardingTestamentaryEntity?.testamentaryEntityName ||
                'Sub-trust'
            ))
        : `Sub-trust`;
    case AiSuggestionKind.Asset:
      return 'asset' in suggestion
        ? (suggestion.asset?.assetTitle ?? 'Asset')
        : 'Asset';
    case AiSuggestionKind.Liability:
      return 'liability' in suggestion
        ? (suggestion.liability?.liabilityName ?? 'Liability')
        : 'Liability';
    case AiSuggestionKind.LifeInsurance:
      return 'Life Insurance';
    case AiSuggestionKind.DpDeathEventGrantor_1DiesFirst:
    case AiSuggestionKind.DpDeathEventGrantor_2DiesFirst:
    case AiSuggestionKind.DpRecipientTestamentaryEntity:
    case AiSuggestionKind.SubtrustType:
      // TODO: Need to delete/cleanup. We don't suggest these anymore.
      return '';

    // Subtrust-related suggestions
    case AiSuggestionKind.SubtrustCreatedFrom:
      return 'Created from';
    case AiSuggestionKind.SubtrustGstStatus:
      return 'GST status';
    case AiSuggestionKind.SubtrustLifetimeBeneficiary:
      return 'Lifetime beneficiary';
    case AiSuggestionKind.SubtrustRemainderBeneficiary:
      return 'Remainder beneficiary';
    case AiSuggestionKind.SubtrustSuccessorTrustee:
      return 'Successor trustee';
    case AiSuggestionKind.SubtrustTrustee:
      return 'Trustee';
    case AiSuggestionKind.SubtrustTrustAdvisor:
      return 'Trust advisor';

    default:
      throw new UnreachableError({
        case: suggestion.kind,
        message: `Unsupported suggestionKind ${suggestion.kind}`,
      });
  }
}

function entityTypeToPrincipalLabel(
  entityType: EntityType | null | undefined
): string {
  switch (entityType) {
    case 'qprt':
    case 'grat':
    case 'slat':
    case 'ilit':
    case 'revocable-trust':
    case 'irrevocable-trust':
      return 'grantor';
    case 'individual-account':
    case 'joint-account':
    case 'retirement-account':
    case 'custodial-account':
    case 'qualified-tuition-account':
    case 'insurance-account':
      return 'owner';
    case 'clt':
    case 'crt':
    case 'daf':
    case 'private-foundation':
      return 'donor';
    case 'sole-proprietorship':
    case 'c-corp':
    case 's-corp':
    case 'llc':
    case 'lp':
    case 'gp':
      return 'beneficial owner';
    default:
      // If the entityType is not recognized, we don't need to return a label;
      // we'll just show the suggestion's name as-is.
      return '';
  }
}
